import { athleteAnswer, bodyComposition, Gender, IBodyAssessment, IBodyFatAdjustments } from 'fitbeat.models';
import {
  athleteAdjustmentRange,
  bodyFatAdjustPercentages,
  bodyFatProposedTrendline,
  safetyNetMaxLowerBorder,
  safetyNetMaxUpperBorder,
} from '../../config';
import { calcBMI } from './TargetUtils';

export class BodyFatAdjustmentCalculations {
  public static adjustBodyFat = (
    rawBodyFat: number,
    bodyCompositionType: bodyComposition,
    height: number,
    weight: number,
    gender: Gender,
    assessment: IBodyAssessment,
  ) => {
    let bodyFatAdjustments = BodyFatAdjustmentCalculations.InitialiseBodyFatAdjustments();

    const bodyTypeAdjustment = BodyFatAdjustmentCalculations.getBodyTypeAdjustment(bodyCompositionType);

    const bodyTypeAdjustedBodyFat = rawBodyFat - bodyTypeAdjustment;

    const athleteAdjustment = BodyFatAdjustmentCalculations.getAthleteAdjustment(
      bodyCompositionType,
      gender,
      bodyTypeAdjustedBodyFat,
      assessment.athleteMode,
    );

    const bodyConditionedBodyFat = rawBodyFat - athleteAdjustment - bodyTypeAdjustment;

    const safetyNetAdjustment =
      BodyFatAdjustmentCalculations.getSafetyNetBodyFatAdjustment(
        height,
        weight,
        bodyCompositionType,
        gender,
        bodyConditionedBodyFat,
        assessment.athleteMode,
      );

    const safetyNetAdjustedBodyFat = bodyConditionedBodyFat + safetyNetAdjustment;

    const minimumBodyFatCorrectionAdjustment =
      BodyFatAdjustmentCalculations.getMinimumBodyFatCorrectionAdjustment(
        safetyNetAdjustedBodyFat,
        bodyCompositionType,
      );

    const adjustedBodyFat =
      rawBodyFat -
      athleteAdjustment -
      bodyTypeAdjustment +
      safetyNetAdjustment +
      minimumBodyFatCorrectionAdjustment;

    bodyFatAdjustments =
      BodyFatAdjustmentCalculations.PopulateBodyFatAdjustments(
        rawBodyFat,
        athleteAdjustment,
        bodyTypeAdjustment,
        safetyNetAdjustment,
        minimumBodyFatCorrectionAdjustment,
        adjustedBodyFat,
        bodyCompositionType,
        gender,
      );

    return bodyFatAdjustments;
  }

  public static InitialiseBodyFatAdjustments = () => {
    return {
      rawPreAdjustmentBodyFat: 0,
      athleteAdjustment: 0,
      bodyTypeAdjustment: 0,
      safetyNetAdjustment: 0,
      adjustedBodyFat: 0,
      minimumBodyFatCorrectionAdjustment: 0,
      proposedTrendLine: {
        x: 0,
        y: 0,
      },
    };
  }

  public static getSafetyNetBodyFatAdjustment(
    height: number,
    weight: number,
    bodyType: bodyComposition,
    gender: Gender,
    bodyConditionAdjustedBodyFat: number,
    athleteMode?: athleteAnswer,
  ) {
    let safetyNetBodyFat = bodyConditionAdjustedBodyFat;
    const heightMeter = height / 100;
    const bmi = calcBMI(heightMeter, weight);
    const proposedBodyFatTrendline =
      BodyFatAdjustmentCalculations.getBodyFatProposedTrendline(
        bodyType,
        gender,
      );
    const proposedBodyFat =
      proposedBodyFatTrendline.x * bmi - proposedBodyFatTrendline.y;
    const upperBorder = proposedBodyFat + safetyNetMaxUpperBorder;
    const lowerBorder = proposedBodyFat - safetyNetMaxLowerBorder;

    safetyNetBodyFat =
      bodyConditionAdjustedBodyFat > upperBorder
        ? upperBorder
        : safetyNetBodyFat;

    if (athleteMode === athleteAnswer.no || athleteMode === null) {
      safetyNetBodyFat =
        bodyConditionAdjustedBodyFat < lowerBorder
          ? lowerBorder
          : safetyNetBodyFat;
    }

    return bodyConditionAdjustedBodyFat - safetyNetBodyFat === 0
      ? 0
      : (bodyConditionAdjustedBodyFat - safetyNetBodyFat) * -1;
  }

  public static getBodyTypeAdjustment(bodyCompositionType: bodyComposition) {
    return bodyFatAdjustPercentages[bodyCompositionType].firstSubtraction;
  }

  public static getAthleteAdjustment(
    bodyCompositionType: bodyComposition,
    gender: Gender,
    bodyFat: number,
    athleteMode?: athleteAnswer,
  ) {
    const isAthlete = athleteMode === athleteAnswer.isConditionedAthlete;

    if (isAthlete) {
      const athleteBodyFatAdjustmentRange =
        BodyFatAdjustmentCalculations.getAthleteBodyFatAdjustmentRange(
          gender,
          bodyFat,
          bodyCompositionType,
        );

      const adjustment = athleteBodyFatAdjustmentRange ? athleteBodyFatAdjustmentRange.adjustment : 0;

      return adjustment;
    } else {
      return 0;
    }
  }

  private static getAthleteBodyFatAdjustmentRange(
    gender: Gender,
    bodyFat: number,
    bodyCompositionType: bodyComposition,
  ) {
    const adjustmentEntry = athleteAdjustmentRange.find((entry) => (
      entry.gender === gender &&
      bodyComposition[entry.bodyType] === bodyComposition[bodyCompositionType] &&
      (
        (entry.minBodyFat ? (bodyFat < entry.minBodyFat && !entry.maxBodyFat) : false) ||
        (entry.maxBodyFat ? (bodyFat > entry.maxBodyFat && !entry.minBodyFat) : false) ||
        ((entry.maxBodyFat && entry.minBodyFat) ? (bodyFat >= entry.minBodyFat && bodyFat <= entry.maxBodyFat) : false)
      )
    ));

    return adjustmentEntry;
  }

  private static getMinimumBodyFatCorrectionAdjustment(
    adjustedBodyFat: number,
    bodyCompositionType: bodyComposition,
  ) {
    const isBelowMinimumBodyFat =
      adjustedBodyFat <= bodyFatAdjustPercentages[bodyCompositionType].min;
    return isBelowMinimumBodyFat
      ? bodyFatAdjustPercentages[bodyCompositionType].min - adjustedBodyFat
      : 0;
  }

  private static getBodyFatProposedTrendline = (
    bodyType: bodyComposition,
    gender: Gender,
  ) => {
    if (gender === Gender.Male && bodyType === bodyComposition.meso) {
      return bodyFatProposedTrendline.maleMeso;
    } else if (gender === Gender.Male) {
      return bodyFatProposedTrendline.male;
    } else {
      return bodyFatProposedTrendline.female;
    }
  }

  private static PopulateBodyFatAdjustments(
    rawBodyFat: number,
    athleteAdjustment: number,
    bodyTypeAdjustment: number,
    safetyNetAdjustment: number,
    minimumBodyFatCorrectionAdjustment: number,
    adjustedBodyFat: number,
    bodyType: any,
    gender: Gender,
  ) {
    const proposedBodyFatTrendline =
      BodyFatAdjustmentCalculations.getBodyFatProposedTrendline(
        bodyType,
        gender,
      );

    const bodyFatAdjustment: IBodyFatAdjustments = {
      rawPreAdjustmentBodyFat: rawBodyFat,
      athleteAdjustment,
      bodyTypeAdjustment,
      safetyNetAdjustment,
      minimumBodyFatCorrectionAdjustment,
      adjustedBodyFat,
      proposedTrendLine: {
        x: proposedBodyFatTrendline.x,
        y: proposedBodyFatTrendline.y,
      },
    };

    return bodyFatAdjustment;
  }
}
